import { Events } from '@utilities';
import { AbstractDispatcher } from '@abstracts';

export default class Breakpoint extends AbstractDispatcher {
  static _instance;

  constructor(options = {}) {
    super();

    Object.assign(this, {
      _value: null,
      _lastValue: null,
      _activeIndex: 0,
      _currentGroups: [],
      _lastGroups: [],
      _groups: {},
      _breakpoints: [],
      _options: {
        ...{ debug: false },
        ...options,
      },
    });

    //singleton validation
    if (Breakpoint._instance)
      throw Error('Breakpoint Error: Use Breakpoint.instance instead of new.');
    Breakpoint._instance = this;

    this._resizeHandler = this._resizeHandler.bind(this);

    this._value = this._getJSONFromCSS();
    this._groups = this._value.groups;

    // set active groups
    this._currentGroups = this._getMatchingGroups(this._value.active);

    // Create breakpoint constant and change value if users has specified some
    this._setBreakpoints(this._value.breakpoints);
    this._resizeHandler();

    // Init resize
    window.addEventListener('resize', Events.debounce(this._resizeHandler), {
      passive: true,
    });

    if (this._value.active === '') {
      throw 'Breakpoint error: Warning no breakpoint set with CSS inside body:after';
    }
  }

  /**
   * function trigger - Trigger event(s) according to active breakpoint(s)
   * @param {boolean} forceGroupTrigger - Force group triggers even if they are currently active, use this with caution
   *
   */
  static trigger(forceGroupTrigger = false) {
    let breakpoint = Breakpoint.instance;
    breakpoint.dispatch({ type: breakpoint._value.active });

    // Group query trigger
    if (breakpoint._groups !== null) {
      breakpoint._currentGroups = breakpoint._getMatchingGroups(
        breakpoint._value.active
      );

      // We make sure that only a match will trigger an event
      if (breakpoint._currentGroups.length > 0) {
        for (var i = 0, l = breakpoint._currentGroups.length; i < l; i++) {
          if (
            breakpoint._lastGroups.indexOf(breakpoint._currentGroups[i]) ===
              -1 ||
            forceGroupTrigger
          ) {
            breakpoint.dispatch({
              type: breakpoint._currentGroups[i],
            });
          }
        }
      }
      breakpoint._lastGroups = breakpoint._currentGroups;
    }
  }

  /**
   * function dispatch - AbstractDispatcher.dispatch override to log what was dispatched
   *
   * @param {object} event - Event object
   *
   */
  dispatch(event) {
    super.dispatch(event);
    if (this._options.debug) console.info('BREAKPOINT: ' + event.type);
  }

  /**
   * function on - Add event listener Breakpoint instance
   *
   * @param {String} type - Event's type
   * @param {Function} listener - callback
   *
   */
  static on(type, listener) {
    Breakpoint.instance.addListener(type, listener);
  }

  /**
   * function off - Remove event listener from Breakpoint instance
   *
   *
   * @param {String} type - Event's type
   * @param {Function} listener - callback used when on was called
   *
   */
  static off(type, listener) {
    Breakpoint.instance.removeListener(type, listener);
  }

  /**
   * Test if active breakpoint is lower than a specific breakpoint
   *
   * @param {String} breakpoint - Breakpoint to test
   * @return {Boolean} Test value
   */
  static isLowerThan(breakpoint) {
    return (
      Breakpoint.instance._activeIndex <
      Breakpoint.instance._breakpoints.indexOf(breakpoint)
    );
  }

  /**
   * Test if active breakpoint is equal to a specific breakpoint
   *
   * @param {String} breakpoint - Breakpoint to test
   * @return {Boolean} Test value
   *
   */
  static isEqualTo(breakpoint) {
    return (
      Breakpoint.instance._activeIndex ===
      Breakpoint.instance._breakpoints.indexOf(breakpoint)
    );
  }

  /**
   * function isHigherThan - Test if active breakpoint is higher than a specific breakpoint
   *
   * @param {String} breakpoint - Breakpoint to test
   * @return {Boolean} Test value
   *
   */
  static isHigherThan(breakpoint) {
    return (
      Breakpoint.instance._activeIndex >
      Breakpoint.instance._breakpoints.indexOf(breakpoint)
    );
  }

  /**
   * function isInGroup - Test if active value is in specified group
   *
   * @param {String} groupName - Group to test
   * @return {Boolean} Presence of not of the current value in the specified group
   *
   */
  isInGroup(groupName) {
    let groups = this._getMatchingGroups(this._value.active);

    for (let i = 0, l = groups.length; i < l; i++) {
      if (groups[i] === groupName) return true;
    }

    return false;
  }

  /**
   * function getValue - Get active value
   * @return {String}
   *
   */
  static getValue() {
    return Breakpoint.instance._value.active;
  }

  /**
   * function isGroupActive - Get status of a group
   * @return {String}
   *
   */
  static isGroupActive(name) {
    return Breakpoint.instance._currentGroups.indexOf(name) !== -1;
  }

  /**
   * function getLastValue - Get last active value
   * @return {String}
   *
   */
  static getLastValue() {
    return Breakpoint.instance._lastValue;
  }

  /**
   * function getBreakpoints - Get breakpoints default value
   * @return {String[]}
   */
  static getBreakpoints() {
    return Breakpoint.instance._breakpoints;
  }

  /**
   * function _setBreakpoints - Change breakpoints default value and create breakpoint constant
   * @param {String[]>}
   */
  _setBreakpoints(opts) {
    if (opts instanceof Array) {
      for (var i = 0, len = opts.length; i < len; i++) {
        this._breakpoints[i] = opts[i];
      }
    }
  }

  /**
   * function _getMatchingGroups - Get groups that contains queries that contains the current browser query
   *
   * @param {string} value - Media query string
   * @return {array[]} Array containing matching group(s)
   */
  _getMatchingGroups(value) {
    let groups = [];
    for (var key in this._groups) {
      if (this._groups.hasOwnProperty(key)) {
        for (let i = 0, len = this._groups[key].length; i < len; i++) {
          if (this._groups[key][i] === value) {
            groups.push(key);
          }
        }
      }
    }

    // If we have no matches, we return an empty array
    return groups;
  }

  /**
   * Resize handler
   */
  _resizeHandler() {
    // Save last breakpoint
    this._lastValue = this._value.active;

    // Refresh value from CSS
    this._value = this._getJSONFromCSS();

    this._activeIndex = this._breakpoints.indexOf(this._value.active);
    // Do not call trigger() onload, otherwise all listeners will be saved after the event has been dispatched
    if (this._lastValue !== this._value.active && this._lastValue !== null) {
      Breakpoint.trigger();
    }
  }

  _getJSONFromCSS() {
    let data = String(
      window
        .getComputedStyle(document.querySelector('body'), ':after')
        .getPropertyValue('content')
    )
      .replace(/\\"/g, '"')
      .toString();

    data = data.substring(1, data.length - 1);

    return JSON.parse(data);
  }

  /**
   * get singleton instance
   * @returns {Breakpoint}  instance's Breakpoint
   */
  static get instance() {
    if (typeof Breakpoint._instance === 'undefined') {
      throw Error(
        'Breakpoint error: Breakpoint has to be instantiated first via new Breakpoint();'
      );
    }
    return Breakpoint._instance;
  }
}
